/**
 * Detalhe do ticket no portal (GET /portal/tickets/:n): cabeçalho com
 * número/título/status/mesa/datas, descrição, feed de comentários PÚBLICOS e
 * composer para responder (POST /portal/tickets/:n/comments) — escondido
 * quando o ticket não está mais aberto (aviso "Ticket fechado") e quando é
 * somente leitura (mesa espelhada): aí aparece o contato da empresa.
 */
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { ArrowLeft, Lock, SendHorizonal } from 'lucide-react';
import type { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { EmptyState, PageSpinner, Spinner } from '@/components/ui/misc';
import { TicketStatusChip } from '@/features/tickets/detail-status-chip';
import { formatDateTime } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import type { PortalTicketDetail as PortalTicketDetailData } from '@erp/shared';
import { portalSheetView } from '@/features/tickets/ticket-sheet-view';
import { useIsImpersonating } from '@/auth/impersonation';
import { ReadOnlyNotice } from '@/features/impersonation/read-only-notice';
import { errorMessage, portalTicketQuery, usePortalComment } from './portal-queries';
import { PortalLink, portalPath } from './portal-nav';

export function PortalTicketDetail() {
  // Rota registrada pelo W1 como /portal/tickets/$ticketNumber — o param não
  // é tipado neste pacote (ver portal-nav.tsx), daí o strict: false + cast.
  const params = useParams({ strict: false }) as { ticketNumber?: string };
  const ticketNumber = Number(params.ticketNumber ?? 0);
  const detail = useQuery(portalTicketQuery(ticketNumber));

  return (
    <div className="space-y-4">
      <PortalLink
        to={portalPath.tickets}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Voltar para meus tickets
      </PortalLink>

      {detail.isPending ? (
        <PageSpinner />
      ) : detail.isError ? (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState
            message={
              (detail.error as unknown as ApiError).status === 404
                ? 'Ticket não encontrado.'
                : `Erro ao carregar o ticket: ${errorMessage(detail.error)}`
            }
          />
        </div>
      ) : (
        <TicketBody ticket={detail.data} />
      )}
    </div>
  );
}

function TicketBody({ ticket }: { ticket: PortalTicketDetailData }) {
  const open = ticket.statusKind === 'open' || ticket.statusKind === 'custom';
  const impersonating = useIsImpersonating();

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="rounded-lg border bg-card p-5 shadow-xs">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-foreground">
              <span className="mr-2 text-primary tabular-nums">#{ticket.ticketNumber}</span>
              {ticket.title}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-muted-foreground">
              <span>{ticket.deskName}</span>
              <span aria-hidden>·</span>
              <span>{ticket.stageName}</span>
              <span aria-hidden>·</span>
              <span className="tabular-nums">Criado em {formatDateTime(ticket.createdAt)}</span>
              {ticket.closedAt ? (
                <>
                  <span aria-hidden>·</span>
                  <span className="tabular-nums">Fechado em {formatDateTime(ticket.closedAt)}</span>
                </>
              ) : null}
            </div>
          </div>
          <TicketStatusChip kind={ticket.statusKind} name={ticket.statusName} />
        </div>

        <div className="mt-4 border-t pt-4">
          <h2 className="text-[13px] font-semibold text-muted-foreground">Descrição</h2>
          <Description html={ticket.description} sheet={ticket.sheet} />
        </div>
      </div>

      {/* Comentários públicos */}
      <div className="rounded-lg border bg-card p-5 shadow-xs">
        <h2 className="text-sm font-semibold text-foreground">
          Comentários
          {ticket.comments.length > 0 ? (
            <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
              ({ticket.comments.length})
            </span>
          ) : null}
        </h2>

        {ticket.comments.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Nenhum comentário ainda.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {ticket.comments.map((c) => (
              <li key={c.id} className="rounded-md border bg-background p-3">
                <div className="flex items-center gap-2">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                    {(c.authorName.trim().charAt(0) || '?').toUpperCase()}
                  </span>
                  <span className="truncate text-[13px] font-semibold text-foreground">
                    {c.authorName}
                  </span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                    {formatDateTime(c.createdAt)}
                  </span>
                </div>
                <p className="mt-1.5 text-sm break-words whitespace-pre-wrap text-foreground">
                  {c.body}
                </p>
              </li>
            ))}
          </ul>
        )}

        {open && !ticket.readOnly ? (
          impersonating ? (
            <ReadOnlyNotice className="mt-4">
              Sessão de depuração: aqui o cliente veria a caixa para responder. Responder fica
              desligado — nada é escrito em nome dele.
            </ReadOnlyNotice>
          ) : (
            <Composer ticketNumber={ticket.ticketNumber} />
          )
        ) : !open ? (
          <div className="mt-4 flex items-center gap-2 rounded-md border bg-muted px-3 py-2.5 text-sm text-muted-foreground">
            <Lock className="size-4 shrink-0" aria-hidden />
            {ticket.statusKind === 'canceled'
              ? 'Ticket cancelado — não é mais possível responder.'
              : 'Ticket fechado — não é mais possível responder.'}
          </div>
        ) : (
          <ContactNotice contact={ticket.contact} />
        )}
      </div>
    </div>
  );
}

/**
 * A ficha vira "rótulo: valor"; o resto é HTML sanitizado, como na tela da
 * equipe. Até 2026-09-28 o portal mostrava a descrição como texto puro, e a
 * ficha aparecia com as tags cruas (`<p>ID_SGR: …<br>`).
 *
 * Spec 031: a ficha é a que a API manda (`sheet`, com a correção da equipe e
 * nunca o solicitante); o ticket aberto à mão com a ficha também a mostra, e a
 * descrição livre aparece abaixo dela quando não é a própria ficha do worker.
 * Contra a API anterior (sem `sheet`), a leitura da descrição, como antes.
 */
function Description({ html, sheet }: { html: string | null; sheet: PortalTicketDetailData['sheet'] }) {
  const terms = useTerms();
  const view = portalSheetView({ description: html, sheet }, terms);
  const hasText = !!html && html.trim() !== '';
  if (view.entries.length === 0 && !hasText) {
    return <p className="mt-1.5 text-sm text-muted-foreground">Sem descrição.</p>;
  }
  return (
    <>
      {view.entries.length > 0 ? (
        <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-[max-content_1fr]">
          {view.entries.map((entry) => (
            <div key={entry.label} className="contents">
              <dt className="text-muted-foreground">{entry.label}</dt>
              <dd className="break-words text-foreground">{entry.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {view.showDescription && hasText ? (
        <div
          className={`mt-1.5 max-w-none break-words text-sm leading-relaxed text-foreground [&_a]:text-primary [&_a]:underline [&_p]:mb-2${
            view.entries.length > 0 ? ' border-t pt-2' : ''
          }`}
          // Sanitizado: a descrição é HTML vindo da equipe, do e-mail e do TiFlux
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
        />
      ) : null}
    </>
  );
}

/**
 * Ticket que o cliente não pode responder pelo portal (espelhado do sistema
 * anterior): em vez de um botão que sempre falha, diz como falar com a equipe.
 */
function ContactNotice({ contact }: { contact: PortalTicketDetailData['contact'] }) {
  const team = contact?.name ? `a equipe ${contact.name}` : 'a equipe';
  const channels = [contact?.phone, contact?.email].filter(Boolean) as string[];
  return (
    <div className="mt-4 rounded-md border bg-muted px-3 py-2.5 text-sm text-muted-foreground">
      <p>
        Este atendimento é conduzido por {team}. Para falar sobre ele
        {channels.length > 0 ? ':' : ', use os canais de atendimento de sempre.'}
      </p>
      {channels.length > 0 ? (
        <p className="mt-1 font-medium text-foreground">{channels.join(' · ')}</p>
      ) : null}
    </div>
  );
}

function Composer({ ticketNumber }: { ticketNumber: number }) {
  const send = usePortalComment(ticketNumber);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const value = body.trim();
    if (!value || send.isPending) return;
    setError(null);
    send.mutate(value, {
      onSuccess: () => setBody(''),
      onError: (e) =>
        setError(
          errorMessage(e) === 'ticket_not_open'
            ? 'O ticket foi fechado e não aceita mais respostas.'
            : `Não foi possível enviar: ${errorMessage(e)}`
        ),
    });
  };

  return (
    <div className="mt-4 border-t pt-4">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Escreva sua resposta…"
        aria-label="Responder ao ticket"
        className="min-h-20"
      />
      {error ? (
        <p className="mt-1.5 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-2 flex justify-end">
        <Button onClick={submit} disabled={send.isPending || body.trim() === ''}>
          {send.isPending ? (
            <Spinner className="size-4 border-primary-foreground" />
          ) : (
            <SendHorizonal className="size-4" aria-hidden />
          )}
          Responder
        </Button>
      </div>
    </div>
  );
}
